import { useState } from 'react'
import { ChevronDownIcon } from 'lucide-react'
import useFaqs from '@/hooks/useFaqs.jsx'
import SEO from '@/components/shared/SEO'
import KeyInformation from '@/components/KeyInformation'
import USPBanner from '@/components/USPBanner'
import CTAButton from '@/components/shared/CTAButton'

// Export empty FAQ data for backward compatibility
export const faqsData = []

const FAQsPage = () => {
  const { faqs, loading, error } = useFaqs()
  const [openIndex, setOpenIndex] = useState(0)

  const toggleFaq = index => {
    setOpenIndex(openIndex === index ? -1 : index)
  }

  if (loading) {
    return (
      <div className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="animate-pulse">
            <div className="bg-muted mb-6 h-8 w-1/3 rounded"></div>
            <div className="space-y-4">
              <div className="bg-muted h-4 rounded"></div>
              <div className="bg-muted h-4 w-5/6 rounded"></div>
              <div className="bg-muted h-4 w-4/6 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="bg-destructive/10 border-destructive text-destructive rounded-lg border p-6">
            <h2 className="mb-2 text-xl font-bold">Error Loading FAQs</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!faqs || faqs.length === 0) {
    return (
      <div className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="bg-muted rounded-lg p-6">
            <h2 className="mb-2 text-xl font-bold">No FAQs Available</h2>
            <p>
              We&apos;re currently updating our FAQ section. Please check back
              soon.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1">
      {faqs && faqs.length > 0 && (
        <SEO
          title="Car Finance Claims FAQ - Frequently Asked Questions | Your Claim Matters"
          description="Get answers to common questions about car finance mis-selling claims. Learn about eligibility, compensation, and the claims process. Expert guidance from Your Claim Matters."
          keywords="car finance claims FAQ, PCP claims questions, HP claims help, car finance compensation FAQ, mis-sold finance questions"
          url="/faqs"
          image="/Image/Car-photo-illustration-claims1.webp"
          imageAlt="Car finance claims frequently asked questions"
          faq={true}
          faqs={faqs.map(faq => ({
            question: faq.question,
            answer: faq.answerText,
          }))}
          breadcrumbs={[
            { name: 'Home', url: '/' },
            { name: 'FAQs', url: '/faqs' },
          ]}
        />
      )}

      <USPBanner />

      {/* Hero Section */}
      <section className="relative flex min-h-[80vh] items-center justify-center bg-[url('/Image/Car-photo-illustration-claims1.webp')] bg-cover bg-[35%_center] bg-no-repeat md:min-h-[82vh] md:bg-[center_bottom_20%]">
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 py-6 text-center md:py-8">
          <h1 className="mb-3 text-3xl leading-tight font-black tracking-tight text-white uppercase sm:text-4xl md:mb-4 md:text-6xl">
            Frequently Asked Questions
          </h1>
          <p className="mx-auto max-w-xl text-base leading-relaxed text-white sm:text-lg md:max-w-3xl md:text-xl">
            Get answers to the most common questions about car finance
            mis-selling claims. Find out if you&apos;re eligible for
            compensation and learn about the claims process.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        className="w-full pt-24 pb-4"
        style={{ backgroundColor: '#c0e0e0' }}
      >
        <div className="mx-auto max-w-4xl px-4">
          <div className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <>
                <div
                  key={index}
                  className="border-foreground bg-background rounded-lg border shadow-sm"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="flex w-full items-center justify-between p-6 text-left focus:outline-none"
                    aria-expanded={openIndex === index}
                  >
                    <span className="text-foreground text-xl font-bold">
                      {faq.question}
                    </span>
                    <ChevronDownIcon
                      className={`text-muted-foreground h-5 w-5 flex-shrink-0 transition-transform duration-300 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openIndex === index && (
                    <div className="border-muted border-t px-6 pt-4 pb-6">
                      <div className="text-foreground text-base leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
                {(index + 1) % 6 === 0 && (
                  <div key={`cta-${index}`} className="py-8 text-center">
                    <CTAButton />
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      </section>

      {/* Key Facts Section */}
      <KeyInformation />
    </div>
  )
}

export default FAQsPage
